/**
 * Was macht der „Hey Vespera"-Lauscher gerade? (23.09.2026, Ticket 2503)
 *
 * Martin: *„Wie kann ich sehen, dass das funktioniert? Da passiert genau gar
 * nichts. Ich sehe nichts."* — Der Lauscher lief im Nativen, und niemand
 * konnte sehen, ob er zuhört, wartet oder gar nicht anläuft. Ab Build 67
 * meldet er jeden Wechsel (Ereignis `wachwortSpur`); hier wird die letzte
 * Meldung gehalten und in einen Satz übersetzt, den man versteht.
 */

interface WachwortZustand {
  /** Die rohe Zeile des Lauschers, z. B. „lauscht" oder „wartet: web-mikro". */
  zeile: string;
  /** Wann sie kam (ms seit 1970). */
  seit: number;
}

let letzter: WachwortZustand | null = null;

/** Was der Punkt an der Sprechtaste zeigt. */
type WachwortArt = "lauscht" | "wartet" | "aus" | "stoerung";

/**
 * Build 69 schreibt „hoert Sprache" mit Leerzeichen. Das ist ein Schlüssel,
 * kein Satz für Martin — verglichen wird deshalb die Token-Form.
 */
function token(zeile: string): string {
  return zeile.trim().toLowerCase().replace(/\s+/g, "-");
}

/**
 * „satz mitgenommen: …" oder „satz: nur anlass (…)" — was nach dem Wach-Wort
 * aus dem Satz wurde. (Build 77, Ticket 2536) Die Zeilen tragen Längen und
 * Gründe, nie den Text.
 */
function istSatzZeile(zeile: string): boolean {
  return zeile.startsWith("satz mitgenommen") || zeile.startsWith("satz: ");
}

/**
 * Das Wach-Wort ist gefallen: „gehoert" — oder ab Build 77 (Ticket 2564)
 * „gehoert-nach-pause: 1400ms", wenn „Hey … Vespera" über eine Pause
 * hinweg erkannt wurde. Die Zahl ist die Wartezeit, nie ein Wort.
 */
function istTreffer(zeile: string): boolean {
  // Build 84: „gehoert-modell: score=0.93" — das eigene Wortmodell hat gehört.
  // Ticket 2753: „gehoert-zu-zweit: score=0.87" — die Erkennung schrieb nur
  // „hey", das Modell hielt kurz: ZUSAMMEN gehört, keiner allein genug. Ohne
  // diese Zeile stünde der Punkt bei einem echten Treffer auf Störung.
  return (
    zeile === "gehoert" ||
    zeile.startsWith("gehoert-nach-pause") ||
    zeile.startsWith("gehoert-modell") ||
    zeile.startsWith("gehoert-zu-zweit")
  );
}

/**
 * „modell: max=0.12", „modell: geladen", „modell: apple bestaetigt",
 * „modell: doppelt …" (Build 84): das Wortmodell meldet sich, höchstens
 * einmal je Minute mit dem höchsten Wert. Es hört zu — keine Störung, und
 * die Zahl gehört ins Protokoll, um die Schwelle an Martins Stimme zu
 * stellen. Nur Zahlen, nie ein Wort.
 */
function istModellZeile(zeile: string): boolean {
  return zeile.startsWith("modell: ");
}

/**
 * „anrede gemerkt: endergebnis 830ms nach erneuerung" oder „… teilergebnis"
 * (Build 77, Nachprüfung zu Ticket 2564): Das Erkannte endete mit „hey" —
 * und auf welchem Weg iOS es abschloss. Belegt am Gerät, welcher Weg
 * „Hey … Vespera" zerschneidet. Nur Art und Zeit, nie ein Wort.
 */
function istAnredeZeile(zeile: string): boolean {
  return zeile.startsWith("anrede gemerkt");
}

/**
 * „neubeginn: anfrage erneuert, vorlauf 2000ms, pause 1310ms" — oder
 * „gebremst", „kein vorlauf", „gehindert (…)". (Build 80, Tickets 2536/2564)
 * Die Erkennung hat nach einer Pause neu angesetzt; der Lauscher hört weiter.
 * Seit der Nachprüfung folgt darauf kein „lauscht" mehr — als Störung
 * gelesen, stünde der Punkt beim Sprechen auf Störung. Nur Zahlen, nie ein
 * Wort. Nach dem Gerätetest drosseln (Befund der Nachprüfung).
 */
function istNeubeginnZeile(zeile: string): boolean {
  return zeile.startsWith("neubeginn: ");
}

/**
 * „[mikro-motor] start 48000 hz wachwort" — der Mikrofon-Motor meldet sich
 * (Ticket 2822, ab der App nach Build 93). Er kommt über denselben Weg wie
 * der Lauscher (`wachwortSpur`), ist aber NICHT der Lauscher: seine Zeile
 * geht ins Protokoll, ohne den Zustand des Punkts zu ändern. Nur Wörter und
 * Zahlen, das Präfix ist Teil der Zeile.
 */
export function istMotorZeile(zeile: string): boolean {
  return zeile.trim().startsWith("[mikro-motor]");
}

/**
 * Der letzte Stand der Tonsitzung, wie der Mikrofon-Motor ihn meldet: „[mikro-motor] unterbrochen“ bzw. „wieder“
 * (PawbotMikroMotor.swift, `unterbrechung`/`telefonVorbei`). (Prüfung 06.10.2026)
 *
 * Nur für die Zeile `[tts-neu]` (tts-queue.ts): Am 05.10. um 10:55 lief das Vorlesen an, 1,1 s nachdem der Motor
 * „wieder“ meldete, und von 30 Wachhund-Fällen seit 29.09. lagen 18 in einem Zustand „unterbrochen“ — ein Hinweis,
 * kein Beweis. Mit dem Zustand in der Zeile zeigt der nächste Gerätefall, ob ein frischer Spieler während einer
 * Unterbrechung überhaupt klingt. Ohne Motor (Browser, ältere App) bleibt es bei „-“.
 */
let motorSitzung: { wort: "unterbrochen" | "wieder"; seit: number; audio?: "ja" | "nein" } | null = null;

/** Eine Zeile des Motors ansehen; nur „unterbrochen“, „wieder“ und die Ursache danach ändern den gemerkten Stand. */
export function merkeMotorZeile(zeile: string, jetzt = Date.now()): void {
  const z = zeile.trim();
  if (!istMotorZeile(z)) return;
  const wort = z.slice("[mikro-motor]".length).trim();
  if (wort === "unterbrochen" || wort === "wieder") {
    motorSitzung = { wort, seit: jetzt };
    return;
  }
  // „ursache andere sitzung audio nein“ folgt auf „unterbrochen“ (PawbotMikroMotor.swift, `ursacheZeile`).
  const audio = /^ursache .* audio (ja|nein)$/.exec(wort)?.[1];
  if (motorSitzung?.wort === "unterbrochen" && (audio === "ja" || audio === "nein")) motorSitzung.audio = audio;
}

/** „unterbrochen/12.3s“ (seit wann), oder „-“, solange der Motor nichts dazu gemeldet hat. */
export function motorSitzungText(jetzt = Date.now()): string {
  if (!motorSitzung) return "-";
  return `${motorSitzung.wort}/${(Math.max(0, jetzt - motorSitzung.seit) / 1000).toFixed(1)}s`;
}

/**
 * Lief beim letzten „unterbrochen“ anderer Ton (`isOtherAudioPlaying`)? „ja“/„nein“ — „-“ ohne Unterbrechung,
 * nach „wieder“ oder ohne Ursachenzeile. (08.10.2026, Pegel nicht messbar)
 *
 * Der Unterschied, der das Journal 01.–08.10. trennt: Unterbrach der Griff der Sprechtaste (getUserMedia) den
 * laufenden Motor mit „audio nein“, stand danach in 21 von 25 Drücken die Tonverarbeitung der Seite (Pegel und
 * Diktat, `rechentakt-tot`; als erster toter Druck einer Reihe 17 von 21) — mit „audio ja“ als erster in keinem von
 * 93. Steht in `rechentakt-…` (rechentakt.ts, `taktZusatz`).
 */
export function motorAudioText(): string {
  return motorSitzung?.wort === "unterbrochen" ? (motorSitzung.audio ?? "-") : "-";
}

/** Nur für Tests. */
export function _motorSitzungVergessen(): void {
  motorSitzung = null;
}

/** Eine Meldung des Lauschers → die Art des Punkts. */
export function wachwortArt(zeile: string): WachwortArt {
  if (zeile === "lauscht" || zeile === "angeschaltet" || istTreffer(zeile)) return "lauscht";
  // Build 69: Diagnose während des Lauschens — er hört ja zu.
  if (token(zeile) === "hoert-sprache" || zeile.startsWith("fast: ")) return "lauscht";
  // Build 77 (Ticket 2536): der Satz nach dem Wach-Wort — ein Treffer, keine Störung.
  if (istSatzZeile(zeile)) return "lauscht";
  if (istAnredeZeile(zeile) || istNeubeginnZeile(zeile) || istModellZeile(zeile)) return "lauscht";
  if (zeile.startsWith("wartet: ")) return "wartet";
  if (zeile === "ausgeschaltet") return "aus";
  return "stoerung";
}

/** Eine Meldung des Lauschers übernehmen. Kurze Übergänge zählen nicht. */
/**
 * Welche Zeile des Lauschers geht ins Server-Protokoll? (23.09.2026, Ticket 2544)
 *
 * Bis heute: jede Zeile höchstens alle fünf Minuten. Das verschluckte
 * Zustandswechsel — „lauscht → wartet: tonbelegung → lauscht" meldete das
 * zweite „lauscht" erst Minuten später, und das Protokoll las sich wie
 * „fünf Minuten taub" (19:21–19:26), obwohl er 0–6 s nach dem Vorlesen
 * wieder zuhörte. Auch „gehoert" um 19:19 fehlte, weil es 19:17 schon da war.
 *
 * Jetzt:
 * - ein ZUSTANDSWECHSEL (lauscht, wartet: …, Störung) geht immer durch;
 * - ein TREFFER („gehoert", „gefallen …") geht immer durch;
 * - RAUSCHEN („hoert Sprache", „fast: …") höchstens alle fünf Minuten je
 *   Zeile — und es ändert den Zustand nicht. Sonst meldete ein lautes Auto
 *   alle 50 Sekunden „hoert Sprache" und gleich danach wieder „lauscht".
 */
export function wachwortMelder(
  abstandMs = 300_000,
): (zeile: string, jetzt: number) => boolean {
  const zuletzt = new Map<string, number>();
  let stand: string | null = null;
  return (zeile, jetzt) => {
    const z = zeile.trim();
    if (!z) return false;
    // Ticket 2822: der Motor meldet nur Wechsel (gleiche Zeilen fasst schon
    // das Native zusammen) — jede geht durch und berührt `stand` nicht.
    if (istMotorZeile(z)) return true;
    const rauschen = token(z) === "hoert-sprache" || z.startsWith("fast: ");
    if (!rauschen) {
      // Ticket 2536: was aus dem Satz wurde, ist Teil des Treffers.
      // Ticket 2564 (Nachprüfung): die gemerkte Anrede ebenso — gedrosselt
      // wäre sie genau in der Minute weg, in der Martin es zweimal versucht.
      // Build 80: die Neubeginn-Zeilen ebenso — der Gerätetest zählt sie.
      if (
        istTreffer(z) ||
        z.startsWith("gefallen") ||
        istSatzZeile(z) ||
        istAnredeZeile(z) ||
        istNeubeginnZeile(z) ||
        istModellZeile(z)
      ) {
        zuletzt.set(z, jetzt);
        return true;
      }
      // „lauscht" und „angeschaltet" sind derselbe Zustand.
      const neu = wachwortArt(z) === "lauscht" ? "lauscht" : z;
      if (neu !== stand) {
        stand = neu;
        zuletzt.set(z, jetzt);
        return true;
      }
    }
    if (jetzt - (zuletzt.get(z) ?? Number.NEGATIVE_INFINITY) < abstandMs) return false;
    zuletzt.set(z, jetzt);
    return true;
  };
}

/**
 * Wie oft verfehlt „Hey Vespera“, bevor es trifft? (20.10.2026, Martin 22:06: „geht oftmals nur jedes zweite Mal“)
 *
 * Befund Journal 18.–20.10.: Jedes gemeldete Wach-Wort („gefallen sichtbar=ja“) öffnete das Mikro oder schickte den
 * Satz — die Seite verschluckt keinen Ruf. Verloren gehen sie VOR dem Treffer im Lauscher: „anrede gemerkt“, „fast: …
 * (nach einem wort)“, „modell: zeuge …“, „modell: wartet …“ oder ein hoher Minutenwert, während nur das Modell hört —
 * ohne „gehoert“ danach. „fast: …“ drosselt der Melder (oben) auf fünf Minuten; gezählt war es darum nie.
 *
 * Diese Rechnung sammelt solche Fehlversuche und gibt EINE Zeile zurück: beim nächsten Treffer
 * („fehlversuche vor treffer: …“) oder nach `fensterMs` ohne Treffer („fehlversuche ohne treffer: …“). Was in den
 * `TREFFER_NAEHE_MS` vor dem Treffer kam, gehört zum treffenden Ruf selbst. „Ohne treffer“ nur, wenn nicht bloß hohe
 * Minutenwerte dabei waren. Nur Arten, Zahlen und der Zustand des
 * Lauschers (voll / nur-modell / wartet) — nie ein Wort.
 */
export const FEHLVERSUCH_FENSTER_MS = 120_000;
const TREFFER_NAEHE_MS = 3_000;
/** Der Minutenwert des Modells deckt die Minute davor — nach einem Treffer zeigt er den Treffer selbst. */
const MINUTENWERT_MS = 60_000;
/** Schwelle des Wortmodells (PawbotWortModell.SCHWELLE): darunter ist ein Minutenwert kein Fehlversuch. */
const MODELL_SCHWELLE = 0.8;

type FehlversuchArt = "anrede" | "fast" | "fastNachWort" | "zeuge" | "wartet" | "hoch";
const FEHLVERSUCH_ARTEN: FehlversuchArt[] = ["anrede", "fast", "fastNachWort", "zeuge", "wartet", "hoch"];

function fehlversuchArt(z: string): FehlversuchArt | null {
  if (istAnredeZeile(z)) return "anrede";
  if (z.startsWith("fast: ")) return z.endsWith("(nach einem wort)") ? "fastNachWort" : "fast";
  if (z.startsWith("modell: zeuge")) return "zeuge";
  if (z.startsWith("modell: wartet")) return "wartet";
  const max = /^modell: max=(\d+(?:\.\d+)?)/.exec(z)?.[1];
  if (max !== undefined && Number(max) >= MODELL_SCHWELLE) return "hoch";
  return null;
}

/** Hört der Lauscher voll (Erkennung und Modell), nur mit dem Modell (Vespera spricht) oder gar nicht? */
function lauscherStand(z: string): string | null {
  if (z.startsWith("lauscht: nur modell") || z.startsWith("eigene stimme")) return "nur-modell";
  if (z === "lauscht" || z === "angeschaltet") return "voll";
  if (z.startsWith("wartet: ")) return "wartet";
  return null;
}

export function wachwortFehlversuche(
  fensterMs = FEHLVERSUCH_FENSTER_MS,
): (zeile: string, jetzt: number) => string | null {
  let stand = "-";
  let letzterTreffer = Number.NEGATIVE_INFINITY;
  let versuche: Array<{ am: number; art: FehlversuchArt; stand: string }> = [];
  const zeile = (kopf: string, liste: typeof versuche, zeit: string): string => {
    const zahl = (a: FehlversuchArt): number => liste.filter((v) => v.art === a).length;
    const arten = FEHLVERSUCH_ARTEN.map((a) => `${a}=${zahl(a)}`).join(" ");
    const staende = [...new Set(liste.map((v) => v.stand))].join("+");
    return `${kopf}: n=${liste.length} ${arten} zustand=${staende} ${zeit}`;
  };
  return (roh, jetzt) => {
    const z = roh.trim();
    if (!z || istMotorZeile(z)) return null;
    stand = lauscherStand(z) ?? stand;
    let raus: string | null = null;
    const letzter = versuche.at(-1);
    if (letzter && jetzt - letzter.am > fensterMs) {
      // Nur hohe Minutenwerte ohne Treffer sind meist fremde Stimmen (Fernseher, Gespräch) — vor einem Treffer zählen sie.
      if (versuche.some((v) => v.art !== "hoch")) {
        raus = zeile("fehlversuche ohne treffer", versuche, `dauer=${letzter.am - versuche[0].am}ms`);
      }
      versuche = [];
    }
    if (istTreffer(z)) {
      letzterTreffer = jetzt;
      const davor = versuche.filter((v) => v.am < jetzt - TREFFER_NAEHE_MS);
      versuche = [];
      if (davor.length > 0) raus = zeile("fehlversuche vor treffer", davor, `seit=${jetzt - davor[0].am}ms`);
      return raus;
    }
    const art = fehlversuchArt(z);
    if (art && !(art === "hoch" && jetzt - letzterTreffer < MINUTENWERT_MS)) versuche.push({ am: jetzt, art, stand });
    return raus;
  };
}

export function merkeWachwortZustand(zeile: string, jetzt = Date.now()): void {
  const z = zeile.trim();
  // „tritt beiseite" ist ein Übergang — gleich danach kommt „wartet: …".
  // Ticket 2822: eine Zeile des Mikrofon-Motors ist kein Zustand des Lauschers.
  if (!z || z.startsWith("tritt beiseite") || istMotorZeile(z)) return;
  letzter = { zeile: z, seit: jetzt };
  /*
   * 23.09.2026 — Martin: „Ich sehe überhaupt kein Zeichen … weder im Widget
   * noch in der App." Der Zustand stand nur in den Einstellungen. Jetzt trägt
   * ihn der body, und die Sprechtaste zeigt ihn als Punkt (style.css).
   */
  try {
    document.body.dataset.wachwort = wachwortArt(z);
  } catch {
    /* kein document (Tests ohne DOM) */
  }
  if (istTreffer(z)) trefferZeigen();
  try {
    window.dispatchEvent(new CustomEvent("voice-v8:wachwort-zustand", { detail: letzter }));
  } catch {
    /* kein window (Tests) */
  }
}

/**
 * Wie lange die Sprechtaste nach einem Treffer pulsiert. (25.09.2026)
 *
 * Martin: *„Manches Mal passiert dann gar nichts. Kann man da ein Symbol
 * oder einen Marker setzen … ein kleines Pips oder irgendein Zeichen auf
 * dem Display, damit ich weiß, ob ich warten oder es noch einmal sagen
 * muss."* Ton und Vibration kommen nativ im Moment der Erkennung (ab
 * Build 82, PawbotWachWort.swift `bestaetigen`); hier das Zeichen auf dem
 * Display. Es kommt mit der Zeile „gehoert", also sofort — nicht erst,
 * wenn der Satz danach fertig ist.
 */
export const TREFFER_ANZEIGE_MS = 3_000;
let trefferUhr: ReturnType<typeof setTimeout> | null = null;

function trefferZeigen(): void {
  try {
    document.body.dataset.wachwortTreffer = "ja";
  } catch {
    return;
  }
  if (trefferUhr) clearTimeout(trefferUhr);
  trefferUhr = setTimeout(() => {
    trefferUhr = null;
    try {
      delete document.body.dataset.wachwortTreffer;
    } catch {
      /* egal */
    }
  }, TREFFER_ANZEIGE_MS);
}

export function wachwortZustand(): WachwortZustand | null {
  return letzter;
}

/** Nur für Tests. */
export function _wachwortZustandVergessen(): void {
  letzter = null;
}

function uhrzeit(ms: number): string {
  return new Date(ms).toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit" });
}

/**
 * Der Zustand als Satz für die Einstellungen.
 *
 * ⚠️ Ohne Meldung heißt NICHT „aus": Bis Build 66 meldete der Lauscher gar
 *    nichts. Das muss der Satz sagen, sonst sucht man den Fehler am Schalter.
 */
export function wachwortZustandText(z: WachwortZustand | null): string {
  if (!z) {
    return "Noch keine Rückmeldung vom Lauscher. Sie kommt erst ab App-Version 67 — in TestFlight aktualisieren.";
  }
  const zeile = z.zeile;
  if (zeile === "lauscht" || zeile === "angeschaltet") return "Hört zu — sag „Hey Vespera“.";
  if (zeile.startsWith("gehoert-nach-pause")) {
    return `Hat dich um ${uhrzeit(z.seit)} gehört — „Hey“ und „Vespera“ auch mit Pause dazwischen.`;
  }
  // Ticket 2753: die Pause gehört nur zu „gehoert-nach-pause" — nicht zum
  // Modell und nicht zu „zu zweit".
  if (istTreffer(zeile)) return `Hat dich um ${uhrzeit(z.seit)} gehört.`;
  if (zeile.startsWith("satz mitgenommen")) {
    return `Hat um ${uhrzeit(z.seit)} deinen Satz nach „Hey Vespera“ mitgenommen.`;
  }
  if (zeile.startsWith("satz: ")) {
    return `Hat dich um ${uhrzeit(z.seit)} gehört — der Satz danach kam nicht mit, das Mikro öffnet sich wie bisher.`;
  }
  if (token(zeile) === "hoert-sprache") return "Hört zu — und versteht gerade Sprache.";
  if (istAnredeZeile(zeile)) return "Hört zu — hat „Hey“ gehört und wartet kurz auf „Vespera“.";
  if (istNeubeginnZeile(zeile)) return "Hört zu — die Erkennung hat nach einer Pause neu angesetzt.";
  if (zeile.startsWith("fast: ")) {
    // Ab Build 77 (Nachprüfung) nur der Kandidat, das Wort davor bleibt am
    // Gerät — „(nach einem wort)" sagt nur, DASS eins davor stand.
    const kandidat = zeile.slice("fast: ".length).replace(/\s*\(nach einem wort\)$/, "");
    return `Fast: verstanden hat es „${kandidat}“ — sag „Hey“ davor, z. B. „Hey Vespera“.`;
  }
  if (zeile === "ausgeschaltet") return "Aus.";
  if (zeile.startsWith("wartet: ")) {
    const grund = zeile.slice("wartet: ".length);
    const warum: Record<string, string> = {
      tonbelegung: "gerade läuft Ton (Vorlesen, Musik oder ein Telefonat)",
      tagesaufnahme: "die Tagesaufnahme hat das Mikrofon",
      "web-mikro": "die Sprechtaste hält das Mikrofon noch (bis zu 45 Sekunden nach einer Antwort)",
      // Build 77 (Ticket 2547): eine Vorlesung hält die Tonsitzung.
      vorlesen: "Vespera liest gerade vor",
      // Build 81 (Tickets 2502/2503): eigene Wiedergabe — das Mikrofon bleibt
      // beim Lauscher, nur die Erkennung ruht (sonst hörte sie Vesperas Stimme).
      wiedergabe: "Vespera spricht gerade; das Mikrofon bleibt bereit",
      // Ticket 2822: der Mikrofon-Motor setzt gerade neu an (Umbau der
      // Tonsitzung, Unterbrechung) — er meldet sich von selbst.
      "mikro-motor": "das Mikrofon startet gerade neu",
    };
    return `Wartet — ${warum[grund] ?? grund}. Danach hört es von selbst wieder zu.`;
  }
  if (zeile.startsWith("kein Sprachmodell")) {
    return "Kein deutsches Sprachmodell auf dem iPhone. Vermutlich hilft: iPhone-Einstellungen › Allgemein › Tastatur › Diktierfunktion einschalten (Deutsch).";
  }
  if (zeile.startsWith("keine Erlaubnis")) {
    return "Keine Erlaubnis für Mikrofon oder Spracherkennung — in den iPhone-Einstellungen bei Vespera freigeben.";
  }
  if (zeile.startsWith("Motor startet nicht")) {
    return `Das Mikrofon lässt sich für den Lauscher nicht öffnen (${zeile.slice("Motor startet nicht: ".length)}).`;
  }
  if (zeile.startsWith("Aufgabe endet")) {
    return `Die Erkennung bricht ab (${zeile.slice("Aufgabe endet: ".length)}) — sie setzt sich von selbst neu an.`;
  }
  return zeile;
}
